import s from './solution-overview.scss';
import { config } from '../../../../config';
import { composer } from '../../../../component-decorators/composer';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { biLogger } from '../../../../bi';
import { Card, Tag, Text, TextButton, InfoIcon, Box } from '@wix/design-system';
import { Add } from '@wix/wix-ui-icons-common';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { PreviewModeContext } from '../../preview-elements';
import { AppMarketLink, Path } from '../../../..';
import { useConfigContext } from '../../../../config-context';
import React from 'react';
import {
  allCountriesCodes,
  getCountryISO2,
} from '../../../standalone-header/header-user-details/header-desktop-user-details/header-desktop-languages-popover/header-desktop-languages-popover-content/languages';
import { ModalNames } from '../../../modals/modals';
import { isWixApp, wixChatBotLink } from '../../../../common/utils';
import type {
  WebSolution,
  WebSolutionBase,
  WSTag,
} from '@wix/ambassador-marketplace/types';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { OtherCountries } from './other-countries/other-countries-options';
import {
  categoryClick,
  appMarketSiteRequirementsTagClick,
} from '@wix/bi-logger-market/v2';

export interface ITagsBoxProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  tags: WSTag[];
  appId: string;
  appName?: string;
  isRequiredWixPremium?: boolean;
  includeCountries?: string[];
  excludeCountries?: string[];
  includeLanguages?: string[];
  excludeLanguages?: string[];
  allowLanguageCustomization?: boolean;
  requiredApps?: any;
  contactMail?: string;
  baseInfo?: WebSolutionBase;
  assetsMap?: any;
  company?: any;
  webSolutionData?: any;
  onSupportClick?: any;
}

export const dtoToTagsBoxProps = (solutionDto: WebSolution): ITagsBoxProps => {
  const tags = filterTags(solutionDto.baseInfo.Tags);
  const appId = solutionDto.baseInfo.id;
  const isRequiredWixPremium =
    solutionDto.baseInfo &&
    solutionDto.baseInfo.pricing &&
    solutionDto.baseInfo.pricing.isRequiredWixPremium;
  const includeCountries = solutionDto.includeCountries;
  const excludeCountries = solutionDto.excludeCountries;
  const excludeLanguages = solutionDto.excludeLanguages;
  const allowLanguageCustomization = solutionDto.allowLanguageCustomization;
  const requiredApps = solutionDto.requiredApps || [];
  const appName = solutionDto.baseInfo.name;
  const contactMail =
    solutionDto.supportEmails && solutionDto.supportEmails.join(';');
  const includeLanguages = solutionDto.includeLanguages;
  const baseInfo = solutionDto.baseInfo;
  const assetsMap = solutionDto.assetsMap;
  const company = solutionDto.company;
  const webSolutionData = solutionDto.webSolutionData;

  return {
    tags,
    appId,
    isRequiredWixPremium,
    includeCountries,
    excludeCountries,
    excludeLanguages,
    allowLanguageCustomization,
    requiredApps,
    appName,
    contactMail,
    includeLanguages,
    baseInfo,
    assetsMap,
    company,
    webSolutionData,
  };
};

export const removeDuplicateNames = (tags: WSTag[]) => {
  if (tags && tags.length) {
    return tags.filter((obj, pos, arr) => {
      return arr.map((mapObj) => mapObj.name).indexOf(obj.name) === pos;
    });
  }

  return [];
};

const filterCollections = (tags: WSTag[]) => {
  if (tags && tags.length) {
    return tags.filter((tag) => tag.type === 'COLLECTION' && !tag.isHidden);
  }
  return [];
};

const filterTags = (tags: WSTag[]) => {
  if (tags && tags.length) {
    return removeDuplicateNames(filterCollections(tags));
  }
  return [];
};

const tagNavigate = (tag: WSTag) => {
  config.goto({
    path: Path.COLLECTION,
    slug: tag?.slug,
    referral: 'app_tag',
  });
};

export const FeaturedIn = composer()
  .withMarketplaceStore()
  .withTranslation()
  .compose(({ tags, t, marketplaceStore }: ITagsBoxProps) => {
    const { isPreview } = PreviewModeContext();

    return tags && tags.length > 0 ? (
      <>
        <div data-testid="tags-box" />
        <Text
          size="small"
          weight="bold"
          className={s.tagBoxTitle}
          style={{ marginBottom: '6px' }}
        >
          {t('solution.page.tagsBoxTitle')}
        </Text>
        <div className={s.tagBoxSection}>
          {tags.map((tag) => (
            <AppMarketLink
              to={`${Path.COLLECTION}/${tag.slug}`}
              onClick={() => {
                biLogger.report(
                  categoryClick({
                    category: tag.slug.replace(/-/g, '_'),
                    msid: marketplaceStore.metaSiteId,
                    origin: 'more_apps_like_this',
                    tag_name: tag.slug.replace(/-/g, '_'),
                    tag_type: 'collection',
                  }),
                );
                tagNavigate(tag);
              }}
            >
              <Tag
                dataHook={`tag-hook-${tag.slug}`}
                key={tag.slug}
                id={tag.slug}
                onClick={(x) => {}}
                removable={false}
                theme="standard"
                className={s.solutionTag}
                size="small"
              >
                {tag.name}
              </Tag>
            </AppMarketLink>
          ))}
        </div>
      </>
    ) : isPreview ? (
      <div data-testid="tags-box">
        <div className={s.tagBoxSection}>
          <Text
            size="small"
            weight="bold"
            className={s.tagBoxTitle}
            style={{ marginBottom: '6px' }}
          >
            {t('solution.page.tagsBoxTitle')}
          </Text>
          <Tag
            key="newest"
            id="newest"
            onClick={(x) => {}}
            removable={false}
            theme="standard"
            className={s.solutionTag}
          >
            {t('solution.page.tagsBoxPreviewTag')}
          </Tag>
        </div>
      </div>
    ) : null;
  });

export const SiteRequirements = composer()
  .withTranslation()
  .compose(
    ({ t, isRequiredWixPremium, appId, requiredApps }: ITagsBoxProps) => {
      const { isPreview } = PreviewModeContext();
      return (requiredApps && requiredApps.length) || isRequiredWixPremium ? (
        <div className={s.tagBoxSection}>
          <div style={{ display: 'flex' }} className={s.availabilityTitle}>
            <Text size="small" weight="bold" className={s.tagBoxTitle}>
              {t('solution.page.tagsBoxSiteReq')}
            </Text>
            <SiteReqTooltip />
          </div>
          {requiredApps.length ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ paddingRight: '6px' }}> - </span>
                <div>
                  {requiredApps.map((app, index) => {
                    return (
                      <div style={{ marginTop: '6px' }}>
                        <AppMarketLink
                          to={`${app.slug}`}
                          onClick={() => {
                            biLogger.report(
                              appMarketSiteRequirementsTagClick({
                                app_id: appId,
                                required_app_id: app.id,
                              }),
                            );
                            config.goto({
                              path: Path.WEB_SOLUTION,
                              slug: app.slug,
                            });
                          }}
                        >
                          <TextButton size="small" as="a" key={app.name}>
                            {app.name}
                          </TextButton>
                        </AppMarketLink>
                        {requiredApps[(index as number) + 1] ? (
                          <span> / &nbsp;</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          {isRequiredWixPremium && (
            <div>
              <div style={{ marginTop: '6px' }}>
                <span> - </span>
                <Text size="small" weight="thin">
                  {t('solution.page.tagsBoxSiteReqPremium')}
                </Text>
              </div>
              <div style={{ marginTop: '6px' }}>
                <span> - </span>
                <Text size="small" weight="thin">
                  {t('solution.page.tagsBoxSiteReqDomain')}
                </Text>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {isPreview && requiredApps.length === 0 ? (
            <div className={s.tagBoxSection}>
              <div style={{ display: 'flex' }} className={s.availabilityTitle}>
                <Text size="small" weight="bold" className={s.tagBoxTitle}>
                  {t('solution.page.tagsBoxSiteReq')}
                </Text>
                <SiteReqTooltip />
              </div>
              <Text size="small" weight="thin">
                {t('solution.page.tagsBoxSiteReqPreview1')}
              </Text>
              <br />
              <TextButton
                prefixIcon={<Add />}
                as="a"
                href={`https://dev.wix.com/dc3/my-apps/${appId}/market/info/installation`}
                target="_blank"
              >
                {t('solution.page.tagsBoxSiteReqPreview2')}
              </TextButton>
            </div>
          ) : null}
        </>
      );
    },
  );

export const AppLanguages = composer()
  .withTranslation()
  .compose(
    ({
      t,
      includeLanguages,
      allowLanguageCustomization,
      appId,
    }: ITagsBoxProps) => {
      const langs = includeLanguages ? includeLanguages : [];
      const translatedLangs = langs
        .map((lang) => t(`marketplace.language.${lang.toUpperCase()}`))
        .sort();
      const { isPreview } = PreviewModeContext();

      return translatedLangs && translatedLangs.length ? (
        <div className={s.tagBoxSection}>
          <div style={{ display: 'flex' }} className={s.availabilityTitle}>
            <Text size="small" weight="bold" className={s.tagBoxTitle}>
              {t('solution.page.tagsBoxSupportedLangTitle')}
            </Text>
            <LanguagesTooltip />
          </div>
          <div className={s.appLanguages}>
            {translatedLangs.map((lang, index) => (
              <span style={{ display: 'inline-block' }}>
                <Text size="small" weight="thin">
                  {lang}
                  {translatedLangs[index + 1] && (
                    <span style={{ display: 'inline-block' }}>,&nbsp;</span>
                  )}
                </Text>
              </span>
            ))}
          </div>
          {allowLanguageCustomization && (
            <div className={s.customizeLang}>
              <Text size="small" weight="thin" secondary>
                {t('solution.page.tagsBoxSupportedLangCustomize')}
              </Text>
            </div>
          )}
        </div>
      ) : isPreview ? (
        <div className={s.tagBoxSection}>
          <div style={{ display: 'flex' }} className={s.availabilityTitle}>
            <Text size="small" weight="bold" className={s.tagBoxTitle}>
              {t('solution.page.tagsBoxSupportedLangTitle')}
            </Text>
            <LanguagesTooltip />
          </div>
          <TextButton
            as="a"
            href={`https://dev.wix.com/dc3/my-apps/${appId}/market/info/installation`}
            target="_blank"
          >
            <span>&nbsp;</span>
            {t('solution.page.tagsBoxSupportedLangPreview')}
          </TextButton>
        </div>
      ) : null;
    },
  );

export const AvailabilityTooltip = composer()
  .withTranslation()
  .compose(({ t }: ITagsBoxProps) => {
    return (
      <Box align="center">
        <InfoIcon
          size="small"
          content={t('solution.page.tagsBoxAvailabilityTooltip')}
        />
      </Box>
    );
  });

export const LanguagesTooltip = composer()
  .withTranslation()
  .compose(({ t }: ITagsBoxProps) => {
    return (
      <Box align="center">
        <InfoIcon
          size="small"
          content={t('solution.page.tagsBoxSupportedLangTooltip')}
        />
      </Box>
    );
  });

export const SiteReqTooltip = composer()
  .withTranslation()
  .compose(({ t }: ITagsBoxProps) => {
    return (
      <Box align="center">
        <InfoIcon
          size="small"
          content={t('solution.page.tagsBoxSiteReqTooltip')}
        />
      </Box>
    );
  });

export const AppAvailability = composer()
  .withTranslation()
  .withMarketplaceStore()
  .withExperiments()
  .compose((props: ITagsBoxProps) => {
    const {
      t,
      includeCountries,
      excludeCountries,
      experiments,
      marketplaceStore,
      contactMail,
      appId,
      appName,
    } = props;
    const appFromInstalled =
      marketplaceStore.installedApps &&
      marketplaceStore.installedApps?.find(
        (installedApp) => installedApp.appId === appId,
      );

    const { isMobile } = useConfigContext();

    const allCountries = allCountriesCodes;
    const isWorldWide =
      (includeCountries && includeCountries.length) === 0 &&
      (excludeCountries && excludeCountries.length) === 0;
    const allIncludedCountries =
      includeCountries && includeCountries.length
        ? includeCountries
        : allCountries.filter(
            (country) =>
              excludeCountries && !excludeCountries.includes(country),
          );
    const { countryCode } = useConfigContext();
    const countryCodeISO2 = getCountryISO2(countryCode);
    const { isPreview } = PreviewModeContext();
    const appAvailabeInUserGeo =
      countryCodeISO2 && allIncludedCountries.includes(countryCodeISO2);
    const appNotAvailableInUserGeo =
      countryCodeISO2 && !excludeCountries.includes(countryCodeISO2);
    const translatedIncludeCountries = allIncludedCountries.map((country) =>
      t(`marketplace.country.${country}`),
    );
    const noUserGeo = !countryCodeISO2;
    const madeByWix =
      props.company &&
      props.company.companyBase &&
      isWixApp(props.company.companyBase.id);

    const contactSupportOnClick = () => {
      if (madeByWix) {
        window.open(wixChatBotLink);
      } else {
        marketplaceStore.openModal(ModalNames.SUPPORT_MODAL, {
          app: appFromInstalled ? appFromInstalled : (props as WebSolution),
          closeModal: () => marketplaceStore.closeAllModals(),
          clickSource: 'countriesPopover',
          pageName: 'app_page',
        });
      }
    };

    return (
      <>
        {isWorldWide ? (
          <div
            className={s.tagBoxSection}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Text size="small" weight="bold" className={s.tagBoxTitle}>
              {t('solution.page.tagsBoxAvailabilityTitle')}
            </Text>

            <Text size="small" weight="thin">
              {t('solution.page.tagsBoxAvailabilityWorldwide')}
            </Text>
            {isPreview ? (
              <TextButton
                as="a"
                href={`https://dev.wix.com/dc3/my-apps/${appId}/market/info/installation`}
                target="_blank"
              >
                <span>&nbsp;</span>
                {t('solution.page.tagsBoxAvailabilityPreview')}
              </TextButton>
            ) : null}
          </div>
        ) : translatedIncludeCountries.length === 1 ? (
          <div
            className={s.tagBoxSection}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex' }} className={s.availabilityTitle}>
              <Text size="small" weight="bold" className={s.tagBoxTitle}>
                {t('solution.page.tagsBoxAvailabilityTitle')}
              </Text>
              <AvailabilityTooltip />
            </div>
            <Text size="small" weight="thin">
              {t('solution.page.tagsBoxAvailabilityText6', {
                countryName: translatedIncludeCountries[0],
              })}
            </Text>
          </div>
        ) : appAvailabeInUserGeo ? (
          <div
            className={s.tagBoxSection}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex' }} className={s.availabilityTitle}>
              <Text size="small" weight="bold" className={s.tagBoxTitle}>
                {t('solution.page.tagsBoxAvailabilityTitle')}
              </Text>
              <AvailabilityTooltip />
            </div>
            <Text size="small" weight="thin">
              {t('solution.page.tagsBoxAvailabilityText1', {
                countryName: t(`marketplace.country.${countryCodeISO2}`),
              })}
            </Text>
            <span>&nbsp;</span>
            {translatedIncludeCountries.length > 1 && (
              <>
                <Text size="small" weight="thin">
                  {t('solution.page.tagsBoxAvailabilityText2')}
                </Text>
                <OtherCountries
                  popoverElementTitle={t(
                    'solution.page.tagsBoxAvailabilityText3',
                  )}
                  isMobile={isMobile}
                  popoverTitle={t(
                    'solution.page.tagsBoxAvailabilityPopoverTitle',
                    { appName },
                  )}
                  countries={translatedIncludeCountries}
                  madeByWix={madeByWix}
                  contactMail={contactMail}
                  appId={appId}
                  onSupportClick={contactSupportOnClick}
                />
              </>
            )}
          </div>
        ) : !noUserGeo &&
          (!appAvailabeInUserGeo || appNotAvailableInUserGeo) ? (
          <div
            className={s.tagBoxSection}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex' }} className={s.availabilityTitle}>
              <Text size="small" weight="bold" className={s.tagBoxTitle}>
                {t('solution.page.tagsBoxAvailabilityTitle')}
              </Text>
              <AvailabilityTooltip />
            </div>
            <Text size="small" weight="thin">
              {t('solution.page.tagsBoxAvailabilityText4', {
                countryName: t(`marketplace.country.${countryCodeISO2}`),
              })}
            </Text>
            {translatedIncludeCountries.length > 0 && (
              <>
                <Text size="small" weight="thin">
                  {t('solution.page.tagsBoxAvailabilityText5', {
                    countryName: t(`marketplace.country.${countryCodeISO2}`),
                  })}
                </Text>
                <OtherCountries
                  popoverElementTitle={t(
                    'solution.page.tagsBoxAvailabilityText3',
                  )}
                  isMobile={isMobile}
                  madeByWix={madeByWix}
                  popoverTitle={t(
                    'solution.page.tagsBoxAvailabilityPopoverTitle',
                    { appName },
                  )}
                  countries={translatedIncludeCountries}
                  contactMail={contactMail}
                  appId={appId}
                  onSupportClick={contactSupportOnClick}
                />
              </>
            )}
          </div>
        ) : noUserGeo && translatedIncludeCountries.length > 1 ? (
          <div
            className={s.tagBoxSection}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex' }} className={s.availabilityTitle}>
              <Text size="small" weight="bold" className={s.tagBoxTitle}>
                {t('solution.page.tagsBoxAvailabilityTitle')}
              </Text>
              <AvailabilityTooltip />
            </div>
            <Text size="small" weight="thin">
              {t('solution.page.tagsBoxAvailabilityText8')}
            </Text>
            <OtherCountries
              popoverTitle={t('solution.page.tagsBoxAvailabilityPopoverTitle', {
                appName,
              })}
              popoverElementTitle={t('solution.page.tagsBoxAvailabilityText7')}
              isMobile={isMobile}
              countries={translatedIncludeCountries}
              madeByWix={madeByWix}
              contactMail={contactMail}
              appId={appId}
              onSupportClick={contactSupportOnClick}
            />
          </div>
        ) : (
          <span />
        )}
      </>
    );
  });

export const TagsBox = composer()
  .withTranslation()
  .withMarketplaceStore()
  .withExperiments()
  .compose((props: ITagsBoxProps) => {
    return (
      <Card className={s.tagBoxContainer}>
        <>
          <AppAvailability {...props} />
          <AppLanguages
            includeLanguages={props.includeLanguages}
            allowLanguageCustomization={props.allowLanguageCustomization}
            appId={props.appId}
          />
          <SiteRequirements
            isRequiredWixPremium={props.isRequiredWixPremium}
            appId={props.appId}
            requiredApps={props.requiredApps}
          />
        </>
        <FeaturedIn tags={props.tags} appId={props.appId} />
      </Card>
    );
  });
